<template>
  <div class="inventory-visualization-page nx-page nx-report-page">
    <el-form inline class="filter-bar" :model="query">
      <el-form-item label="库存日期">
        <el-date-picker
          v-model="stockDate"
          type="date"
          placeholder="选择日期"
          value-format="yyyy-MM-dd"
          style="width: 160px"
        />
      </el-form-item>
      <el-form-item label="库区">
        <el-select
          v-model="query.stockCode"
          filterable
          remote
          reserve-keyword
          :remote-method="remoteStore"
          :loading="storeLoading"
          clearable
          placeholder="拼音码/名称检索"
          style="width: 210px"
          @change="onStockChange"
          @visible-change="onStoreVisible"
        >
          <el-option
            v-for="item in storeOptions"
            :key="item.storeNo"
            :label="item.storeName"
            :value="item.storeNo"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="煤种">
        <el-select
          v-model="query.cargoNo"
          filterable
          remote
          reserve-keyword
          :remote-method="remoteCargo"
          :loading="cargoLoading"
          clearable
          placeholder="煤种拼音码"
          style="width: 210px"
          @change="onCargoChange"
          @visible-change="onCargoVisible"
        >
          <el-option
            v-for="item in cargoOptions"
            :key="item.varNo"
            :label="item.varName"
            :value="item.varNo"
          />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" icon="el-icon-search" @click="handleQuery">查询</el-button>
        <el-button icon="el-icon-refresh-left" @click="searchReset">重置</el-button>
      </el-form-item>
    </el-form>

    <div v-if="query.stockCode || query.cargoNo" class="active-filters">
      <el-tag v-if="query.stockCode" size="small" closable @close="clearFilter('stockCode')">库区：{{ stockLabel || query.stockCode }}</el-tag>
      <el-tag v-if="query.cargoNo" size="small" closable @close="clearFilter('cargoNo')">物资：{{ cargoLabel || query.cargoNo }}</el-tag>
    </div>

    <el-row :gutter="12" class="kpi-row">
      <el-col :xs="24" :sm="12" :lg="6">
        <el-card shadow="hover" class="kpi-card">
          <div class="kpi-label">库存总量（吨）</div>
          <div class="kpi-value">{{ formatNum(summary.totalTonnage) }}</div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :lg="6">
        <el-card shadow="hover" class="kpi-card">
          <div class="kpi-label">平均堆存时长（天）</div>
          <div class="kpi-value">{{ summary.avgStockDays }}</div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :lg="6">
        <el-card shadow="hover" class="kpi-card">
          <div class="kpi-label">高风险堆存点位</div>
          <div class="kpi-value">{{ summary.highRiskCount }}</div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :lg="6">
        <el-card shadow="hover" class="kpi-card">
          <div class="kpi-label">覆盖库区数</div>
          <div class="kpi-value">{{ summary.shedCount }}</div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="12" class="chart-row">
      <el-col :xs="24" :lg="12">
        <el-card shadow="hover" class="chart-card">
          <div class="card-head">
            <span class="card-title">煤种分布</span>
          </div>
          <div ref="coalChart" class="chart-box"></div>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="12">
        <el-card shadow="hover" class="chart-card">
          <div class="card-head">
            <span class="card-title">堆存时长分布</span>
          </div>
          <div ref="ageChart" class="chart-box"></div>
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="hover" class="chart-card chart-card--full">
      <div class="card-head">
        <span class="card-title">煤棚库存对比</span>
      </div>
      <div ref="shedChart" class="chart-box chart-box--full"></div>
    </el-card>

    <el-card shadow="hover" class="table-card">
      <div class="card-head">
        <span class="card-title">库存明细</span>
      </div>
      <el-table v-loading="tableLoading" :data="tableRecords" border stripe style="width: 100%">
        <el-table-column prop="shed" label="库区" min-width="110" />
        <el-table-column prop="coalType" label="煤种" min-width="110" />
        <el-table-column prop="storageDays" label="平均库存时长(天)" min-width="120" align="right">
          <template slot-scope="{ row }">{{ formatNum(row.storageDays) }}</template>
        </el-table-column>
        <el-table-column prop="tonnage" label="库存量(吨)" min-width="120" align="right">
          <template slot-scope="{ row }">{{ formatNum(row.tonnage) }}</template>
        </el-table-column>
        <el-table-column prop="riskLevel" label="风险等级" min-width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="riskTagType(row.riskLevel)" size="small">{{ row.riskLevel }}</el-tag>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="pager"
        background
        layout="total, prev, pager, next"
        :total="tableTotal"
        :page-size="query.size"
        :current-page.sync="query.page"
        @current-change="loadDetailPage"
      />
    </el-card>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { fetchDashboard, fetchDetailPage, searchStorePycode, searchVarPycode } from './api'

function defaultStockDate() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function unwrapList(res) {
  const body = res && res.data
  if (!body) return []
  const raw = body.data !== undefined ? body.data : body
  if (Array.isArray(raw)) return raw
  if (raw && Array.isArray(raw.records)) return raw.records
  return []
}

export default {
  name: 'InventoryVisualization',
  data() {
    return {
      stockDate: defaultStockDate(),
      query: {
        stockCode: '',
        cargoNo: '',
        page: 1,
        size: 10
      },
      stockLabel: '',
      cargoLabel: '',
      storeOptions: [],
      cargoOptions: [],
      storeLoading: false,
      cargoLoading: false,
      summary: {
        totalTonnage: 0,
        avgStockDays: 0,
        highRiskCount: 0,
        shedCount: 0
      },
      tableLoading: false,
      tableRecords: [],
      tableTotal: 0,
      coalChart: null,
      ageChart: null,
      shedChart: null
    }
  },
  mounted() {
    this.$nextTick(async () => {
      this.coalChart = echarts.init(this.$refs.coalChart)
      this.ageChart = echarts.init(this.$refs.ageChart)
      this.shedChart = echarts.init(this.$refs.shedChart)
      window.addEventListener('resize', this.handleResize)
      await this.handleQuery()
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    this.coalChart && this.coalChart.dispose()
    this.ageChart && this.ageChart.dispose()
    this.shedChart && this.shedChart.dispose()
  },
  methods: {
    async remoteStore(query) {
      this.storeLoading = true
      try {
        const res = await searchStorePycode(query)
        this.storeOptions = unwrapList(res)
      } catch (e) {
        this.storeOptions = []
      } finally {
        this.storeLoading = false
      }
    },
    onStoreVisible(visible) {
      if (visible && this.storeOptions.length === 0) {
        this.remoteStore('')
      }
    },
    onStockChange(val) {
      if (!val) {
        this.stockLabel = ''
        return
      }
      const hit = this.storeOptions.find((i) => i.storeNo === val)
      this.stockLabel = hit ? hit.storeName : ''
    },
    async remoteCargo(query) {
      this.cargoLoading = true
      try {
        const res = await searchVarPycode(query)
        this.cargoOptions = unwrapList(res)
      } catch (e) {
        this.cargoOptions = []
      } finally {
        this.cargoLoading = false
      }
    },
    onCargoVisible(visible) {
      if (visible && this.cargoOptions.length === 0) {
        this.remoteCargo('')
      }
    },
    onCargoChange(val) {
      if (!val) {
        this.cargoLabel = ''
        return
      }
      const hit = this.cargoOptions.find((i) => i.varNo === val)
      this.cargoLabel = hit ? hit.varName : ''
    },
    basePayload() {
      if (!this.stockDate) {
        this.$message.warning('请选择库存日期')
        return null
      }
      return {
        stockDate: this.stockDate,
        stockCode: this.query.stockCode || undefined,
        cargoNo: this.query.cargoNo || undefined
      }
    },
    async handleQuery() {
      const payload = this.basePayload()
      if (!payload) return
      this.query.page = 1
      await Promise.all([this.loadDashboard(payload), this.loadDetailPage()])
    },
    async loadDashboard(payload) {
      try {
        const { data } = await fetchDashboard(payload)
        const dash = data.data || {}
        this.summary = dash.summary || this.summary

        const coalItems = (dash.coalDistribution && dash.coalDistribution.items) || []
        this.coalChart.setOption(
          {
            tooltip: {
              trigger: 'item',
              formatter: (x) => `${x.name}<br/>库存量：${this.formatNum(x.value)} 吨<br/>占比：${x.percent}%`
            },
            legend: { top: 8 },
            series: [
              {
                type: 'pie',
                radius: ['35%', '65%'],
                center: ['50%', '56%'],
                label: { formatter: '{b}\n{d}%' },
                data: coalItems
              }
            ]
          },
          true
        )

        const buckets = (dash.storageAging && dash.storageAging.buckets) || []
        this.ageChart.setOption(
          {
            color: ['#67C23A'],
            tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
            grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
            xAxis: { type: 'category', data: buckets.map((i) => i.name) },
            yAxis: { type: 'value', name: '吨' },
            series: [{ type: 'bar', barMaxWidth: 44, data: buckets.map((i) => i.value) }]
          },
          true
        )

        const shedItems = (dash.shedStock && dash.shedStock.items) || []
        this.shedChart.setOption(
          {
            color: ['#409EFF'],
            tooltip: {
              trigger: 'axis',
              axisPointer: { type: 'shadow' },
              formatter: (items) => {
                const x = items && items[0]
                if (!x) return ''
                const row = x.data
                const name = row && row.name != null ? row.name : x.name
                const val = row && row.value != null ? row.value : x.value
                return `${name}<br/>库存量：${this.formatNum(val)} 吨`
              }
            },
            grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
            xAxis: { type: 'category', data: shedItems.map((i) => i.name) },
            yAxis: { type: 'value', name: '吨' },
            series: [
              {
                type: 'bar',
                barMaxWidth: 56,
                data: shedItems.map((i) => ({
                  value: i.value,
                  name: i.name,
                  code: i.code
                }))
              }
            ]
          },
          true
        )
      } catch (e) {
        this.$message.error('看板数据加载失败')
      }
    },
    async loadDetailPage() {
      const payload = this.basePayload()
      if (!payload) return
      this.tableLoading = true
      try {
        const { data } = await fetchDetailPage({
          ...payload,
          page: this.query.page,
          size: this.query.size
        })
        const page = data.data || {}
        this.tableRecords = page.records || []
        this.tableTotal = page.total || 0
      } catch (e) {
        this.tableRecords = []
        this.tableTotal = 0
      } finally {
        this.tableLoading = false
      }
    },
    searchReset() {
      this.stockDate = defaultStockDate()
      this.query.stockCode = ''
      this.query.cargoNo = ''
      this.stockLabel = ''
      this.cargoLabel = ''
      this.storeOptions = []
      this.cargoOptions = []
      this.query.page = 1
      this.handleQuery()
    },
    clearFilter(key) {
      this.query[key] = ''
      if (key === 'stockCode') this.stockLabel = ''
      if (key === 'cargoNo') this.cargoLabel = ''
      this.handleQuery()
    },
    riskTagType(level) {
      if (level === '高') return 'danger'
      if (level === '中') return 'warning'
      return 'success'
    },
    formatNum(v) {
      const n = Number(v || 0)
      return n.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
    },
    handleResize() {
      this.coalChart && this.coalChart.resize()
      this.ageChart && this.ageChart.resize()
      this.shedChart && this.shedChart.resize()
    }
  }
}
</script>

<style scoped>
.inventory-visualization-page {
  padding: 0;
  background: transparent;
  --visual-card-gap: 12px;
}
.page-head {
  margin-bottom: 16px;
}
.page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}
.page-desc {
  margin: 6px 0 0;
  color: #909399;
  font-size: 13px;
}
.filter-bar {
  margin-bottom: var(--visual-card-gap);
}
.active-filters {
  margin-bottom: var(--visual-card-gap);
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.kpi-row {
  margin-bottom: 0;
}
.kpi-row .el-col {
  margin-bottom: var(--visual-card-gap);
}
.kpi-card {
  min-height: 96px;
}
.kpi-label {
  color: #909399;
  font-size: 13px;
}
.kpi-value {
  margin-top: 8px;
  color: #303133;
  font-size: 24px;
  font-weight: 700;
}
.chart-row {
  margin-bottom: 0;
}
.chart-row .el-col {
  margin-bottom: var(--visual-card-gap);
}
.chart-row .chart-card {
  margin-bottom: 0;
}
.chart-card {
  min-height: 340px;
  margin-bottom: var(--visual-card-gap);
}
.chart-card--full {
  min-height: 360px;
}
.card-head {
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.card-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}
.chart-box {
  width: 100%;
  height: 280px;
}
.chart-box--full {
  height: 300px;
}
.table-card {
  margin-bottom: 0;
}
.pager {
  margin-top: 12px;
  text-align: right;
}
</style>
